/**
 * Histórico das marcações de um pagamento: o botão das listagens e o modal que ele abre.
 *
 * Um componente só para as quatro listagens de pagamento (tela de Pagamentos, passo 1 da
 * Identificação e os dois modais da tela de Títulos). Cada linha é um acontecimento de uma marcação,
 * com o retrato dela naquele momento: marcada, aprovada, sinalização removida, substituída,
 * liberada porque a fatura foi cancelada ou paga por outro dinheiro, ou pagamento excluído.
 *
 * O modal vai para o `body` por portal. O botão fica dentro da linha da tabela, e a linha da tela
 * de Pagamentos tem `hover:brightness-95`: um `filter` no ancestral vira o bloco de contenção do
 * `position: fixed`, e o modal pulava entre a linha e a tela a cada movimento do mouse.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaHistory, FaTimes } from 'react-icons/fa';

import CommentCell from '../CommentCell';
import Loading from '../../ions/Loading';
import {
    MarkingHistoryEntry,
    getPaymentMarkingHistory,
} from '../../pages/billing/services/billingApi';
import { MODE_LABELS, modeOf } from '../../pages/billing/constants/liquidationModes';
import { paymentActionPromiseLabel } from '../../pages/billing/constants/paymentActionPromise';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatDateTimeBR, formatDateToBR } from '../../../utils/DateUtils';

const NEUTRAL = 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200';

const EVENT_COLORS: Record<string, string> = {
    MARKED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
    APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    UNLINKED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    REPLACED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    PAYMENT_DELETED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    CANCELLED_INVOICE: NEUTRAL,
    PAID_BY_BOLETO: NEUTRAL,
    PAID_BY_OTHER_PAYMENT: NEUTRAL,
};

const TH = 'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase';
const TD = 'px-3 py-2 text-sm align-top text-gray-800 dark:text-gray-100';

const fraction = (entry: MarkingHistoryEntry) => {
    const { installmentNumber, totalInstallments } = entry.marking;
    return installmentNumber && totalInstallments ? `${installmentNumber}/${totalInstallments}` : null;
};

const invoiceLabel = (entry: MarkingHistoryEntry) =>
    entry.invoice.documentTitle || `fatura ${entry.invoice.invoiceId.slice(0, 8)}`;

interface ModalProps {
    paymentId: string;
    onClose: () => void;
    zIndexClass?: string;
}

export const PaymentMarkingHistoryModal: React.FC<ModalProps> = ({ paymentId, onClose, zIndexClass = 'z-50' }) => {
    const [entries, setEntries] = useState<MarkingHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        getPaymentMarkingHistory(paymentId)
            .then(list => { if (active) setEntries(list); })
            .catch((e: any) => { if (active) setError(e?.message || 'Não foi possível carregar o histórico'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [paymentId]);

    return createPortal(
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`}
            onClick={onClose}>
            <div role="dialog" aria-label="Histórico de marcações"
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-6xl w-full max-h-[85vh] flex flex-col"
                onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
                        <FaHistory className="text-blue-500" /> Histórico de marcações
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white">
                        <FaTimes size={20} />
                    </button>
                </div>

                {loading && <Loading />}
                {!loading && error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                {!loading && !error && entries.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Este pagamento ainda não teve nenhuma marcação.</p>
                )}
                {!loading && !error && entries.length > 0 && (
                    <div className="overflow-auto border dark:border-slate-600 rounded">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                                <tr>
                                    <th className={TH}>Quando</th>
                                    <th className={TH}>Acontecimento</th>
                                    <th className={TH}>Fatura</th>
                                    <th className={`${TH} text-right`}>Valor</th>
                                    <th className={TH}>Modo</th>
                                    <th className={TH}>Promessa</th>
                                    <th className={TH}>Por</th>
                                    <th className={TH}>Observações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map(entry => (
                                    <tr key={entry.historyId} className="border-t dark:border-slate-600">
                                        <td className={`${TD} whitespace-nowrap`}>{formatDateTimeBR(entry.occurredAt)}</td>
                                        <td className={TD}>
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${EVENT_COLORS[entry.event] || 'bg-gray-100 text-gray-700'}`}>
                                                {entry.eventFormatted}
                                            </span>
                                            {entry.eventNotes && (
                                                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{entry.eventNotes}</span>
                                            )}
                                        </td>
                                        <td className={TD}>
                                            {invoiceLabel(entry)}
                                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                {entry.invoice.dueDate ? `venc. ${formatDateToBR(entry.invoice.dueDate)}` : ''}
                                                {fraction(entry) ? ` · ${fraction(entry)}` : ''}
                                            </span>
                                        </td>
                                        <td className={`${TD} text-right whitespace-nowrap`}>
                                            {entry.marking.amount != null ? formatBRL(entry.marking.amount) : '—'}
                                        </td>
                                        <td className={TD}>{MODE_LABELS[modeOf(entry.marking.totalPayment)]}</td>
                                        <td className={TD}>{paymentActionPromiseLabel(entry.marking.paymentActionPromise)}</td>
                                        <td className={TD}>{entry.actor ? (entry.actor.name || entry.actor.id) : 'Sistema'}</td>
                                        <td className={TD}><CommentCell notes={entry.marking.notesList} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

interface ButtonProps {
    paymentId: string;
    /** acima do modal de onde o botão foi aberto */
    zIndexClass?: string;
}

const PaymentMarkingHistoryButton: React.FC<ButtonProps> = ({ paymentId, zIndexClass }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button type="button" onClick={() => setOpen(true)}
                title="Histórico de marcações" aria-label="Histórico de marcações"
                className="px-2 py-1 text-gray-500 hover:text-blue-600 align-middle">
                <FaHistory />
            </button>
            {open && (
                <PaymentMarkingHistoryModal paymentId={paymentId} zIndexClass={zIndexClass}
                    onClose={() => setOpen(false)} />
            )}
        </>
    );
};

export default PaymentMarkingHistoryButton;
